/**
 * Tags List Component
 * 
 * Main container for tags list with search, sort, and bulk operations
 * Following the design specifications and accounts feature pattern
 * 
 * Features:
 * - Grid/list display with color badges
 * - Search and sort functionality
 * - Bulk operations
 * - Add/Edit/Delete tag modals
 * - WCAG 2.2 AA compliant
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tagService } from '../../../../services/tag-service';
import { Tag } from '../../../../types/tag.types';
import { Plus, Tag as TagIcon } from 'lucide-react';
import { AddTagModal } from './AddTagModal';
import { EditTagModal } from './EditTagModal';
import { TagGrid } from './TagGrid';
import { SearchBar } from './SearchBar';
import { SortControls } from './SortControls';
import { BulkActionsToolbar } from './BulkActionsToolbar';

export function TagsList(): React.ReactElement {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'count' | 'date'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);

  // Fetch tags
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['tags', searchTerm],
    queryFn: async () => {
      const params: any = {};
      if (searchTerm) params.search = searchTerm;
      
      const result = await tagService.listTags(params);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data;
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddTag = () => {
    setShowAddModal(true);
  };

  const handleEditTag = (tag: Tag) => {
    setEditingTag(tag);
  };

  const handleDeleteTag = async (tag: Tag) => {
    if (window.confirm(`Are you sure you want to delete "${tag.name}"?`)) {
      setIsSubmitting(true);
      const result = await tagService.deleteTag(tag.id);
      setIsSubmitting(false);
      if (!result.error) {
        refetch();
      } else {
        alert(result.error.message);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTags.size === 0) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedTags.size} tags?`)) {
      setIsSubmitting(true);
      const result = await tagService.bulkDeleteTags(Array.from(selectedTags));
      setIsSubmitting(false);
      if (!result.error) {
        setSelectedTags(new Set());
        refetch();
      } else {
        alert(result.error.message);
      }
    }
  };

  const handleBulkEdit = () => {
    // TODO: Implement bulk edit modal
    console.log('Bulk edit tags:', Array.from(selectedTags));
  };

  const handleAddTagSubmit = async (data: any) => {
    setIsSubmitting(true);
    const result = await tagService.createTag(data);
    setIsSubmitting(false);
    if (!result.error) {
      setShowAddModal(false);
      refetch();
    } else {
      alert(result.error.message);
    }
  };

  const handleEditTagSubmit = async (data: any) => {
    if (!editingTag) return;
    setIsSubmitting(true);
    const result = await tagService.updateTag(editingTag.id, data);
    setIsSubmitting(false);
    if (!result.error) {
      setEditingTag(null);
      refetch();
    } else {
      alert(result.error.message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500 dark:text-gray-400">Loading tags...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200">
          Error loading tags: {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  const tags = data?.tags || [];

  return (
    <div className="space-y-6">
      {/* Tags Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <TagIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Tags
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manage your transaction tags
          </p>
        </div>

        {/* Header Actions */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <button
              onClick={handleAddTag}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium text-sm shadow-sm hover:shadow-md transition-all duration-200 flex items-center space-x-2"
              aria-label="Add new tag"
            >
              <Plus className="w-4 h-4" aria-hidden="true" />
              <span>Add Tag</span>
            </button>
          </div>
        </div>

        {/* Search and Sort Controls */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex-1">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search tags..."
            />
          </div>
          <SortControls
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={(by, order) => {
              setSortBy(by as 'name' | 'date' | 'count');
              setSortOrder(order);
            }}
            availableSorts={[
              { value: 'name', label: 'Name' },
              { value: 'count', label: 'Transaction Count' },
              { value: 'date', label: 'Date Created' }
            ]}
          />
        </div>

        {/* Bulk Actions Toolbar */}
        {selectedTags.size > 0 && (
          <BulkActionsToolbar
            selectedCount={selectedTags.size}
            onBulkDelete={handleBulkDelete}
            onBulkEdit={handleBulkEdit}
            onClearSelection={() => setSelectedTags(new Set())}
            availableActions={['delete', 'edit']}
          />
        )}

        {/* Tags Grid */}
        <div className="overflow-hidden -mx-6 -mb-6 mt-6">
          <TagGrid
            tags={tags}
            onEdit={handleEditTag}
            onDelete={handleDeleteTag}
            selectedIds={selectedTags}
            onSelectionChange={setSelectedTags}
            viewMode="grid"
          />
        </div>
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddTagModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddTagSubmit}
          loading={isSubmitting}
        />
      )}

      {editingTag && (
        <EditTagModal
          tag={editingTag}
          onClose={() => setEditingTag(null)}
          onSubmit={handleEditTagSubmit}
          loading={isSubmitting}
        />
      )}
    </div>
  );
}

